import { useCallback, useState } from "react";
import { router } from "@inertiajs/react";
import { Body, PageHeader } from "@sprout/typography";
import { PrimaryButton } from "@sprout/buttons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { TermsOfServiceForm } from "@components/terms_of_service/form";
import useTranslation from "@hooks/use_translation";

type Props = {
  email: string;
  createPath: string;
};

const SignupModal = ({ email, createPath }: Props) => {
  const { t } = useTranslation("misc");

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(() => {
    setIsSubmitting(true);
    router.post(
      createPath,
      {
        email,
        accept_terms: acceptedTerms ? "1" : "0",
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      {
        onError: (e) => {
          console.error(e);
          setError(t("defaultError"));
        },
        onFinish: () => {
          setIsSubmitting(false);
        },
      },
    );
  }, [acceptedTerms, email, createPath, t]);

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-[550px] p-10"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            <PageHeader>{t("newUser.header")}</PageHeader>
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          <div className="flex flex-col gap-[12px]">
            <Body>
              {t("newUser.body")}
              <Body as="span" medium>
                {email}
              </Body>
            </Body>
          </div>
        </DialogDescription>
        <TermsOfServiceForm error={error} onChange={setAcceptedTerms} value={acceptedTerms} />
        <DialogFooter>
          <PrimaryButton onClick={submit} loading={isSubmitting} disabled={!acceptedTerms}>
            {t("submit")}
          </PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SignupModal;
